import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountsStore } from '@/entities/account';
import { useSessionStore } from '@/entities/session';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui';
import { useAppSettingsStore, useMediaQuery } from '@/shared/lib';
import { cn } from '@/shared/utils';
import { useConfirmationModal, useUserProfileDialog } from '@/widgets';
import { useLimitsModal } from '@/widgets/user-profile/limits';
import { useBalanceRows, useDropdownLimits, useUserIdentity } from '../model';
import { UserBalanceTrigger } from './UserBalanceTrigger';
import { UserBalancePanel } from './UserBalancePanel';
import { UserBalanceSkeleton } from './UserBalanceSkeleton';

export function UserBalanceDropdown() {
  const { t } = useTranslation();
  const isTabletUp = useMediaQuery('(min-width: 1068px)');
  const [open, setOpen] = useState(false);

  const { logout } = useSessionStore();
  const general = useAccountsStore(state => state.general);
  const { isBalancesHidden } = useAppSettingsStore();
  const identity = useUserIdentity();
  const balanceRows = useBalanceRows(general);
  const limits = useDropdownLimits({ enabled: identity.isVerified });

  const { openModal, Modal: ConfirmationModal } = useConfirmationModal();
  const { openUserProfileDialog } = useUserProfileDialog();
  const { openLimitsDialog } = useLimitsModal();

  if (identity.isLoading || !general) {
    return (
      <div className="relative w-full max-w-[35.2rem]">
        <UserBalanceSkeleton />
      </div>
    );
  }

  const close = () => setOpen(false);

  const handleOpenSettings = () => {
    close();
    openUserProfileDialog();
  };

  const handleOpenLimits = () => {
    if (!limits.transformedLimitsMap) {
      return;
    }

    close();
    openLimitsDialog({
      onBack: openUserProfileDialog,
      walletId: identity.accountId,
      limitsMap: limits.transformedLimitsMap,
    });
  };

  const handleLogout = () => {
    close();
    openModal({
      title: t('confirmationModal.logout.title'),
      description: t('confirmationModal.logout.description'),
      confirmText: t('confirmationModal.logout.confirmText'),
      cancelText: t('confirmationModal.logout.cancelText'),
      onConfirm: () => logout(),
    });
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <UserBalanceTrigger
            general={general}
            initial={identity.initial}
            shortName={identity.shortName}
            isCorporate={identity.isCorporate}
            isVerified={identity.isVerified}
            isBalancesHidden={isBalancesHidden}
            showName={isTabletUp}
            isOpen={open}
          />
        </PopoverTrigger>

        <PopoverContent
          align={isTabletUp ? 'end' : 'start'}
          sideOffset={8}
          collisionPadding={12}
          className={cn(
            'rounded-[1.2rem] bg-background/[0.94] backdrop-blur-[0.4rem] border-stroke p-[0.4rem] shadow-dropdown!',
            'max-h-[calc(100dvh-10rem)] overflow-y-auto',
            isTabletUp ? 'w-[24rem]' : 'w-[26.8rem]'
          )}>
          <UserBalancePanel
            general={general}
            fullName={identity.fullName}
            accountId={identity.accountId}
            isVerified={identity.isVerified}
            isBalancesHidden={isBalancesHidden}
            isTabletUp={isTabletUp}
            balanceRows={balanceRows}
            limits={limits}
            onOpenSettings={handleOpenSettings}
            onOpenLimits={handleOpenLimits}
            onLogout={handleLogout}
          />
        </PopoverContent>
      </Popover>

      {ConfirmationModal}
    </>
  );
}
